import { Warning, X } from "@phosphor-icons/react";

/**
 * In-app confirmation dialog. Controlled via props.
 *
 * Usage:
 *   const [state, setState] = useState(null);
 *   setState({ title: "Удалить?", description: "...", onConfirm: () => {...} });
 *   <ConfirmDialog state={state} onClose={() => setState(null)} />
 */
export default function ConfirmDialog({ state, onClose }) {
  if (!state) return null;
  const {
    title = "Подтвердите действие",
    description = "",
    confirmLabel = "Удалить",
    cancelLabel = "Отмена",
    tone = "danger", // "danger" | "primary"
    onConfirm,
  } = state;

  const handleConfirm = () => {
    try {
      onConfirm?.();
    } finally {
      onClose();
    }
  };

  const confirmClass =
    tone === "danger"
      ? "bg-[#FF2A00] text-white hover:bg-[#cc2200]"
      : "bg-[#0047FF] text-white hover:bg-[#0036CC]";

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="confirm-dialog-overlay"
    >
      <div
        className="bg-white border border-[#18181b] w-full max-w-md hard-shadow"
        onClick={(e) => e.stopPropagation()}
        data-testid="confirm-dialog"
      >
        <div className="border-b border-[#d4d4d8] px-6 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <Warning
              size={20}
              weight="fill"
              className={tone === "danger" ? "text-[#FF2A00] shrink-0 mt-0.5" : "text-[#0047FF] shrink-0 mt-0.5"}
            />
            <h2 className="text-base font-semibold tracking-tight" data-testid="confirm-dialog-title">
              {title}
            </h2>
          </div>
          <button
            onClick={onClose}
            data-testid="confirm-dialog-close"
            className="p-1.5 border border-[#d4d4d8] hover:bg-[#18181b] hover:text-white hover:border-[#18181b] transition-colors duration-150"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        {description && (
          <div className="px-6 py-5 text-sm text-[#52525b]" data-testid="confirm-dialog-description">
            {description}
          </div>
        )}

        <div className="border-t border-[#d4d4d8] px-6 py-3 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            data-testid="confirm-dialog-cancel"
            className="border border-[#18181b] bg-transparent text-[#18181b] px-4 py-2 text-sm hover:bg-[#18181b] hover:text-white transition-colors duration-150"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            autoFocus
            data-testid="confirm-dialog-confirm"
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm transition-colors duration-150 ${confirmClass}`}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
